// Whole-home composite: places every scanned room into one shared frame and
// renders a per-floor 2D plan + a stacked 3D dollhouse. x/z placement comes from
// each scan's projectTransformValues; floors are clustered from each room.json's
// referenceOriginTransform.y (see composite.ts). Rooms without a curated
// placement fall back to a labeled grid layout. The merged structure.usdz the
// iOS app builds won't render on desktop, so this reconstructs from the scans.

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../lib/storeContext";
import { resolveAsset } from "../lib/assetResolver";
import { buildScanGeometry, squareUpRotation, wallThicknessFromScan } from "../geometry/scanGeometry";
import type { WallThicknessConfig } from "../geometry/scanGeometry";
import { PlanProjection } from "../geometry/projection";
import type { CapturedRoomJSON, ScanGeometry, Vec2 } from "../geometry/types";
import type { ProjectPayload, RoomPayload, ScanPayload } from "../lib/types";
import { Spinner } from "../views/ui";
import { buildComposite, type RoomInput } from "./composite";
import type { CompositeRoom3D } from "./CompositeDollhouse";
import type { RoomSplatData } from "../dollhouse/RoomSplat";
import { listSplats, getSplatJob } from "../lib/apiClient";

const CompositeDollhouse = lazy(() =>
  import("./CompositeDollhouse").then((m) => ({ default: m.CompositeDollhouse })),
);

const INK = "#2B2522";
const ACCENT = "#CA6B4A";
const PAPER = "#FBF7F2";
const GLASS = "#5b93c2";
const VIEW_W = 1000;
const VIEW_H = 700;
const PADDING = 48;

function pathFromPolygon(poly: Vec2[], proj: PlanProjection): string {
  if (poly.length === 0) return "";
  const pts = poly.map((p) => proj.point(p));
  return "M " + pts.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" L ") + " Z";
}

/** The slice of Project.roomOverridesJSON the composite honors (mirrors the iOS ProjectRoomOverrides
 *  shape): per-room manual floor assignment + custom level names. Keys are Room-record UUIDs. */
interface RoomOverrides {
  rooms: Record<string, { floor?: number | null }>;
  levels: Record<string, string>;
}
function parseOverrides(json: string | undefined): RoomOverrides {
  if (!json) return { rooms: {}, levels: {} };
  try {
    const v = JSON.parse(json) as Partial<RoomOverrides>;
    return {
      rooms: v.rooms && typeof v.rooms === "object" ? v.rooms : {},
      levels: v.levels && typeof v.levels === "object" ? v.levels : {},
    };
  } catch {
    return { rooms: {}, levels: {} };
  }
}

/** Pick one representative interior scan per room: newest with a room-json blob. */
interface ScanRow {
  scanId: string;
  roomId: string;
  name: string;
  roomJsonKey: string;
  placement?: number[];
  captureFloorTag?: number | null;
  capturedAt?: number;
  isStairwell?: boolean;
  thickness?: WallThicknessConfig;
}

export function WholeHomeComposite({ projectId }: { projectId: string }) {
  const { store, rev } = useStore();
  void rev;

  const scanRows = useMemo<ScanRow[]>(() => {
    const rows: ScanRow[] = [];
    for (const room of store.rooms(projectId)) {
      const scans = store.scans(room.id); // ascending by createdAt
      for (let i = scans.length - 1; i >= 0; i--) {
        const sp = scans[i].payload as ScanPayload;
        if (sp.spaceKindRaw) continue; // skip outdoor (deck/porch) captures
        const key = scans[i].assets.find((a) => a.kind === "room-json")?.s3Key;
        if (!key) continue;
        const createdMs = Date.parse(sp.createdAt);
        rows.push({
          scanId: scans[i].id,
          roomId: room.id,
          name: (room.payload as RoomPayload).name || "Room",
          roomJsonKey: key,
          placement: sp.projectTransformValues,
          captureFloorTag: sp.captureFloorTag,
          capturedAt: Number.isFinite(createdMs) ? createdMs : undefined,
          isStairwell: sp.isStairwell === true,
          thickness: wallThicknessFromScan(sp),
        });
        break; // one per room
      }
    }
    return rows;
  }, [store, projectId, rev]);

  const keysSig = scanRows.map((r) => r.roomJsonKey).join("|");
  const [geoms, setGeoms] = useState<Map<string, ScanGeometry>>(new Map());
  const [loadedCount, setLoadedCount] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setGeoms(new Map());
    setLoadedCount(0);
    setErr(null);
    if (scanRows.length === 0) return;
    let done = 0;
    Promise.all(
      scanRows.map(async (row) => {
        try {
          const url = await resolveAsset(row.roomJsonKey);
          if (!url) return;
          const json = (await (await fetch(url)).json()) as CapturedRoomJSON;
          if (cancelled) return;
          setGeoms((prev) => {
            const next = new Map(prev);
            next.set(row.scanId, buildScanGeometry(json, row.thickness));
            return next;
          });
        } catch {
          /* skip a room that fails to load — the composite degrades gracefully */
        } finally {
          done += 1;
          if (!cancelled) setLoadedCount(done);
        }
      }),
    ).then(() => {
      if (!cancelled && done === 0) setErr("Could not load any room geometry.");
    });
    return () => {
      cancelled = true;
    };
  }, [keysSig]); // eslint-disable-line react-hooks/exhaustive-deps

  // W4: wall-cropped photoreal splats paired to this project's scans. A splat job carries
  // roomScanId once paired; we pull its model-cropped.ply + alignment.json so the dollhouse can swap
  // the box geometry for the real room. Best-effort — rooms without a paired splat just stay boxes.
  const [splatByScan, setSplatByScan] = useState<Map<string, RoomSplatData>>(new Map());
  const [photoreal, setPhotoreal] = useState(false);
  const scanIdSig = scanRows.map((r) => r.scanId).join("|");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const scanIds = new Set(scanRows.map((r) => r.scanId));
        const jobs = await listSplats();
        const paired = jobs.filter(
          (j) => j.status === "completed" && j.roomScanId && scanIds.has(j.roomScanId),
        );
        const out = new Map<string, RoomSplatData>();
        await Promise.all(
          paired.map(async (j) => {
            try {
              const detail = await getSplatJob(j.splatId);
              // model-cropped.ply is baked into the room frame, so the point cloud renders at identity.
              const cropped = detail.models?.find((m) => m.id === "model-cropped.ply");
              if (!cropped) return;
              out.set(j.roomScanId!, { url: cropped.url });
            } catch { /* skip this one */ }
          }),
        );
        if (!cancelled) setSplatByScan(out);
      } catch { /* no splats / not signed in — dollhouse stays boxes */ }
    })();
    return () => { cancelled = true; };
  }, [scanIdSig]); // eslint-disable-line react-hooks/exhaustive-deps

  // The user's manual floor/level assignments (roomOverridesJSON) — the authoritative split-level
  // truth the iOS dollhouse honors; without this the web re-derived floors from refY and disagreed.
  const overrides = useMemo(
    () => parseOverrides(store.get<ProjectPayload>("PROJECT", projectId)?.payload.roomOverridesJSON),
    [store, projectId, rev],
  );

  const inputs = useMemo<RoomInput[]>(() => {
    const out: RoomInput[] = [];
    for (const row of scanRows) {
      const g = geoms.get(row.scanId);
      if (!g) continue;
      const ro = g.raw.referenceOriginTransform;
      out.push({
        scanId: row.scanId,
        name: row.name,
        geometry: g,
        placement: row.placement,
        refY: ro && ro.length >= 16 ? ro[13] : undefined,
        captureFloorTag: row.captureFloorTag,
        floorOverride: overrides.rooms[row.roomId]?.floor ?? undefined,
        capturedAt: row.capturedAt,
        isStairwell: row.isStairwell,
      });
    }
    return out;
  }, [scanRows, geoms, overrides]);

  const comp = useMemo(() => buildComposite(inputs, overrides.levels), [inputs, overrides]);

  const storyGap = useMemo(() => {
    const hs = inputs.map((i) => i.geometry.ceilingHeightMeters).filter((h) => h > 1).sort((a, b) => a - b);
    const med = hs.length ? hs[Math.floor(hs.length / 2)] : 2.6;
    return med + 0.4;
  }, [inputs]);

  const [floor, setFloor] = useState<number | "all">("all");
  const [tab, setTab] = useState<"plan" | "3d">("plan");
  const [showFurniture, setShowFurniture] = useState(true);

  // Recover if the selected floor disappears (e.g. re-clustering collapses floors
  // after a scan is removed) — otherwise the view would filter to an empty set.
  useEffect(() => {
    if (floor !== "all" && !comp.floors.some((f) => f.index === floor)) setFloor("all");
  }, [comp, floor]);

  const visibleRooms = useMemo(
    () => (floor === "all" ? comp.rooms : comp.rooms.filter((r) => r.floorIndex === floor)),
    [comp, floor],
  );

  // 2D plan scene (projection + paths), recomputed when the visible set changes.
  const planScene = useMemo(() => {
    if (visibleRooms.length === 0) return null;
    let mn = { ...visibleRooms[0].boundsMin };
    let mx = { ...visibleRooms[0].boundsMax };
    for (const r of visibleRooms) {
      mn = { x: Math.min(mn.x, r.boundsMin.x), y: Math.min(mn.y, r.boundsMin.y) };
      mx = { x: Math.max(mx.x, r.boundsMax.x), y: Math.max(mx.y, r.boundsMax.y) };
    }
    const rotation = squareUpRotation(visibleRooms.flatMap((r) => r.walls));
    const proj = new PlanProjection(mn, mx, { width: VIEW_W, height: VIEW_H }, PADDING, rotation);
    return {
      proj,
      rooms: visibleRooms.map((r) => ({
        scanId: r.scanId,
        name: r.name,
        floorPath: r.floorPolygon.length >= 3 ? pathFromPolygon(r.floorPolygon, proj) : "",
        wallPaths: r.thickWalls.map((w) => pathFromPolygon(w, proj)),
        labelAt: proj.point(r.center),
        openings: r.openings.map((o) => {
          const s = proj.point(o.start);
          const e = proj.point(o.end);
          return { kind: o.kind, d: `M ${s.x.toFixed(1)} ${s.y.toFixed(1)} L ${e.x.toFixed(1)} ${e.y.toFixed(1)}` };
        }),
      })),
    };
  }, [visibleRooms]);

  // Pan + zoom (mirrors FloorplanSvg).
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [floor, tab]);

  const rooms3d = useMemo<CompositeRoom3D[]>(
    () =>
      visibleRooms.map((r) => ({
        scanId: r.scanId,
        geometry: geoms.get(r.scanId)!,
        placementMatrix: r.placementMatrix,
        floorIndex: floor === "all" ? r.floorIndex : 0, // isolate selected floor at ground
        splat: splatByScan.get(r.scanId) ?? null,
      })),
    [visibleRooms, geoms, floor, splatByScan],
  );
  const anyPhotoreal = useMemo(() => rooms3d.some((r) => r.splat), [rooms3d]);

  if (scanRows.length === 0) return null;

  const total = scanRows.length;
  const loading = loadedCount < total && inputs.length === 0;

  const vbW = VIEW_W / zoom;
  const vbH = VIEW_H / zoom;
  const vbX = (VIEW_W - vbW) / 2 - pan.x;
  const vbY = (VIEW_H - vbH) / 2 - pan.y;

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-cream p-3">
        <div className="flex items-center gap-2">
          <div className="flex rounded-full bg-cream p-1 text-sm">
            {(["plan", "3d"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`rounded-full px-4 py-1.5 font-medium transition ${
                  tab === t ? "bg-white text-accent shadow-card" : "text-muted"
                }`}
              >
                {t === "3d" ? "Stacked 3D" : "Floor plan"}
              </button>
            ))}
          </div>
          {comp.multiFloor && (
            <div className="flex rounded-full bg-cream p-1 text-sm">
              <button
                onClick={() => setFloor("all")}
                className={`rounded-full px-3 py-1.5 font-medium transition ${
                  floor === "all" ? "bg-white text-accent shadow-card" : "text-muted"
                }`}
              >
                All
              </button>
              {comp.floors.map((f) => (
                <button
                  key={f.index}
                  onClick={() => setFloor(f.index)}
                  className={`rounded-full px-3 py-1.5 font-medium transition ${
                    floor === f.index ? "bg-white text-accent shadow-card" : "text-muted"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted">
          {!comp.anyRealPlacement && <span className="rounded-full bg-amber/20 px-2 py-1 text-ink/70">approx. layout</span>}
          {tab === "3d" && (
            <button
              className="rounded-full bg-white/85 px-3 py-1.5 font-semibold text-ink shadow"
              onClick={() => setShowFurniture((s) => !s)}
            >
              {showFurniture ? "Hide furniture" : "Show furniture"}
            </button>
          )}
          {tab === "3d" && anyPhotoreal && (
            <button
              className={`rounded-full px-3 py-1.5 font-semibold shadow ${photoreal ? "bg-accent text-white" : "bg-white/85 text-ink"}`}
              onClick={() => setPhotoreal((p) => !p)}
              title="Swap box geometry for the wall-cropped photoreal splat where a room has one"
            >
              {photoreal ? "Photoreal: on" : "Photoreal"}
            </button>
          )}
        </div>
      </div>

      {err ? (
        <div className="p-8 text-center text-sm text-muted">{err}</div>
      ) : loading ? (
        <Spinner label={`Loading rooms… ${loadedCount}/${total}`} />
      ) : tab === "plan" ? (
        <div className="relative w-full" style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}` }}>
          <svg
            viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
            className="h-full w-full touch-none select-none"
            style={{ background: PAPER, cursor: dragRef.current ? "grabbing" : "grab" }}
            onWheel={(e) => {
              e.preventDefault();
              setZoom((z) => Math.min(8, Math.max(0.5, z * (e.deltaY < 0 ? 1.1 : 1 / 1.1))));
            }}
            onPointerDown={(e) => {
              (e.target as Element).setPointerCapture?.(e.pointerId);
              dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
            }}
            onPointerMove={(e) => {
              const d = dragRef.current;
              if (!d) return;
              const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
              const sx = VIEW_W / rect.width / zoom;
              const sy = VIEW_H / rect.height / zoom;
              setPan({ x: d.px + (e.clientX - d.x) * sx, y: d.py + (e.clientY - d.y) * sy });
            }}
            onPointerUp={() => {
              dragRef.current = null;
            }}
            onDoubleClick={() => {
              setZoom(1);
              setPan({ x: 0, y: 0 });
            }}
          >
            {planScene?.rooms.map((r) => (
              <g key={r.scanId}>
                {r.floorPath && <path d={r.floorPath} fill={ACCENT} fillOpacity={0.08} stroke="none" />}
                {r.openings.map((o, i) => (
                  <path
                    key={`o${i}`}
                    d={o.d}
                    stroke={o.kind === "window" ? GLASS : ACCENT}
                    strokeOpacity={0.8}
                    strokeWidth={o.kind === "window" ? 2 : 2.5}
                    strokeDasharray={o.kind === "opening" ? "4 3" : undefined}
                    fill="none"
                  />
                ))}
                {r.wallPaths.map((d, i) => (
                  <path key={`w${i}`} d={d} fill={INK} fillOpacity={0.14} stroke={INK} strokeWidth={1.2} strokeLinejoin="round" />
                ))}
                <text
                  x={r.labelAt.x}
                  y={r.labelAt.y}
                  fontSize={12}
                  fontWeight={600}
                  fill={INK}
                  fillOpacity={0.75}
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={{ paintOrder: "stroke" }}
                  stroke={PAPER}
                  strokeWidth={3}
                >
                  {r.name}
                </text>
              </g>
            ))}
          </svg>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between px-3 py-2 text-xs">
            <span className="text-muted">Scroll to zoom · drag to pan · double-click to reset</span>
            <span className="text-muted">{visibleRooms.length} rooms</span>
          </div>
        </div>
      ) : (
        <Suspense fallback={<Spinner label="Loading 3D…" />}>
          <CompositeDollhouse
            rooms={rooms3d}
            storyGap={storyGap}
            floorCount={floor === "all" ? comp.floors.length : 1}
            boundsMin={planScene ? boundsOf(visibleRooms).min : comp.boundsMin}
            boundsMax={planScene ? boundsOf(visibleRooms).max : comp.boundsMax}
            showFurniture={showFurniture}
            photoreal={photoreal}
          />
        </Suspense>
      )}
    </div>
  );
}

function boundsOf(rooms: { boundsMin: Vec2; boundsMax: Vec2 }[]): { min: Vec2; max: Vec2 } {
  if (rooms.length === 0) return { min: { x: -2, y: -2 }, max: { x: 2, y: 2 } };
  let min = { ...rooms[0].boundsMin };
  let max = { ...rooms[0].boundsMax };
  for (const r of rooms) {
    min = { x: Math.min(min.x, r.boundsMin.x), y: Math.min(min.y, r.boundsMin.y) };
    max = { x: Math.max(max.x, r.boundsMax.x), y: Math.max(max.y, r.boundsMax.y) };
  }
  return { min, max };
}
